import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { Mail, AlertCircle, ChevronDown } from 'lucide-react';

const SUPPORTED_LANGS = ['fr', 'de', 'en'];
const LANG_LABELS = { fr: 'Français', de: 'Deutsch', en: 'English' };

const I18N = {
  fr: {
    title: 'Connectez-vous ou inscrivez-vous',
    subtitle: 'Entrez votre email pour accéder à la plateforme',
    email_label: 'Adresse email',
    email_placeholder: 'votre@email.com',
    send_code: 'Envoyer le code',
    sending: 'Envoi en cours...',
    email_hint: 'Un code à 6 chiffres vous sera envoyé par email',
    help: "Besoin d'aide ?",
    contact: 'Contactez le support',
    powered_by: 'Propulsé par',
    no_event: 'Aucun événement sélectionné',
    no_event_desc: "Veuillez accéder à la page de connexion via le lien de votre événement.",
    back_home: "Retour à l'accueil",
    loading: 'Chargement...',
    not_found: "Compte non trouvé. Contactez l'organisateur.",
  },
  de: {
    title: 'Anmelden oder registrieren',
    subtitle: 'Geben Sie Ihre E-Mail ein, um auf die Plattform zuzugreifen',
    email_label: 'E-Mail-Adresse',
    email_placeholder: 'ihre@email.com',
    send_code: 'Code senden',
    sending: 'Wird gesendet...',
    email_hint: 'Ein 6-stelliger Code wird an Ihre E-Mail gesendet',
    help: 'Hilfe benötigt?',
    contact: 'Support kontaktieren',
    powered_by: 'Bereitgestellt von',
    no_event: 'Kein Event ausgewählt',
    no_event_desc: 'Bitte öffnen Sie die Anmeldeseite über den Link Ihres Events.',
    back_home: 'Zur Startseite',
    loading: 'Laden...',
    not_found: 'Konto nicht gefunden. Veranstalter kontaktieren.',
  },
  en: {
    title: 'Log in or sign up',
    subtitle: 'Enter your email to access the platform',
    email_label: 'Email address',
    email_placeholder: 'your@email.com',
    send_code: 'Send code',
    sending: 'Sending...',
    email_hint: 'A 6-digit code will be sent to your email',
    help: 'Need help?',
    contact: 'Contact support',
    powered_by: 'Powered by',
    no_event: 'No event selected',
    no_event_desc: 'Please access the login page via your event link.',
    back_home: 'Back to home',
    loading: 'Loading...',
    not_found: 'Account not found. Contact the organizer.',
  },
};

const getInitialLang = () => {
  const saved = localStorage.getItem('language');
  if (SUPPORTED_LANGS.includes(saved)) return saved;
  const browser = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(browser) ? browser : 'fr';
};

const LoginPage = () => {
  const { event, loading: eventLoading, loadEventBySlug } = useEvent();
  const { sendCode } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const eventSlug = slug || event?.slug || '';

  const [lang, setLang] = useState(getInitialLang);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isVisitorOff, setIsVisitorOff] = useState(false);

  const i = I18N[lang] || I18N.fr;
  const isAccessOpen = event?.access !== '0';

  // Charger l'event depuis le slug si pas déjà chargé
  useEffect(() => {
    if (slug && !event) loadEventBySlug(slug);
  }, [slug, event, loadEventBySlug]);

  // Charger content-display pour vérifier si Visitor = off
  useEffect(() => {
    const eventId = event?.id || event?.subevent_id;
    if (!eventId) return;
    fetch(`/api/content-display/${encodeURIComponent(eventId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const visitorConfig = data.data.find(c => c.type_id === 'Visitor');
          setIsVisitorOff(visitorConfig?.status === 'off');
        }
      })
      .catch(() => {});
  }, [event?.id, event?.subevent_id]);

  const handleLangChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem('language', newLang);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const options = {};
      if (isVisitorOff) {
        options.visitor_check = true;
        options.color_main = event?.color_main || '#1c1b3a';
      }
      await sendCode(email, event?.id || '', event?.name || '', options);
      const verifyPath = eventSlug ? `/event/${eventSlug}/verify` : '/verify';
      navigate(verifyPath, { state: { email, lang } });
    } catch (err) {
      console.error('Send code error:', err);
      setError(err.not_found ? i.not_found : (err.message || "Impossible d'envoyer le code. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  // Chargement de l'event
  if (eventLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{i.loading}</p>
        </div>
      </div>
    );
  }

  // Pas d'event
  if (!event && !slug && !eventLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="text-orange-500 mx-auto mb-4" size={40} />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{i.no_event}</h2>
          <p className="text-gray-600 mb-6">{i.no_event_desc}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            {i.back_home}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">

            {/* Language Selector */}
            <div className="flex justify-end mb-5">
              <div className="relative">
                <select
                  value={lang}
                  onChange={(e) => handleLangChange(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {SUPPORTED_LANGS.map(l => (
                    <option key={l} value={l}>{LANG_LABELS[l]}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Event Logo */}
            {(event?.logo_dark || event?.logo) && (
              <div className="text-center mb-5">
                <img
                  src={event.logo_dark || event.logo}
                  alt={event?.name}
                  className="max-h-20 object-contain mx-auto"
                />
              </div>
            )}

            {/* Titre + Nom événement */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{i.title}</h1>
              {event?.name && (
                <p className="text-blue-600 font-semibold text-lg">{event.name}</p>
              )}
              <p className="text-gray-500 text-sm mt-2">{i.subtitle}</p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Message d'accès fermé */}
            {!isAccessOpen && event?.access_message && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <p className="text-sm text-amber-800 font-medium">{event.access_message}</p>
              </div>
            )}

            {/* Formulaire email */}
            <form onSubmit={isAccessOpen ? handleSendCode : (e) => e.preventDefault()} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {i.email_label}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="text-gray-400" size={20} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={i.email_placeholder}
                    required
                    disabled={loading || !isAccessOpen}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                {isAccessOpen && (
                  <p className="mt-2 text-xs text-gray-500">{i.email_hint}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !email || !isAccessOpen}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>{i.sending}</span>
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    <span>{i.send_code}</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Help */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {i.help}{' '}
              <a href="mailto:hello@mobivent.fr" className="text-blue-600 font-semibold hover:underline">
                {i.contact}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">
            {i.powered_by} <span className="font-semibold">Mobivent</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
